import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, TrendingUp, Users, Calendar, Shield, Star, BarChart3 } from 'lucide-react';
import { useSiteImage } from '@/contexts/SiteImagesContext';

const PropertyManagementPage = () => {
  const heroImage = useSiteImage('hero.property-management', 'https://images.unsplash.com/photo-1668277157922-ecd8330ea809?w=1920&q=80');
  const whyChooseImage = useSiteImage('section.why-choose-lalita', 'https://images.unsplash.com/photo-1711948728889-614d5d0030f8?w=800&q=80');
  const services = [
    {
      icon: TrendingUp,
      title: 'Revenue Optimization',
      description: 'Dynamic pricing, seasonal strategies, and market analysis to maximize your rental income.',
    },
    {
      icon: Users,
      title: 'Guest Management',
      description: 'From inquiry to checkout, we handle all guest communications and ensure 5-star experiences.',
    },
    {
      icon: Calendar,
      title: 'OTA Management',
      description: 'Listings on Airbnb, Booking.com, VRBO, and more with optimized descriptions and photos.',
    },
    {
      icon: Shield,
      title: 'Property Maintenance',
      description: 'Regular inspections, preventive maintenance, and 24/7 emergency support.',
    },
    {
      icon: Star,
      title: 'Professional Marketing',
      description: 'High-quality photography, virtual tours, and compelling property descriptions.',
    },
    {
      icon: BarChart3,
      title: 'Owner Reporting',
      description: 'Monthly reports with detailed financials, occupancy rates, and property updates.',
    },
  ];

  const benefits = [
    'Higher occupancy rates than self-managed properties',
    'Professional 24/7 guest support',
    'Local maintenance team on call',
    'Transparent monthly reporting',
    'No hidden fees or commissions',
    'Flexible management contracts',
  ];

  return (
    <div className="min-h-screen bg-white pt-20" data-testid="property-management-page">
      {/* Hero */}
      <section className="relative py-20 lg:py-28 bg-charcoal overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src={heroImage}
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-white mb-6">
                Property Management<br />
                <span className="italic text-white/80">Done Right</span>
              </h1>
              <p className="font-body text-lg text-white/70 mb-8">
                Let us handle the day-to-day while you enjoy the returns. Our boutique approach ensures your villa gets the attention it deserves.
              </p>
              <Link
                to="/stays"
                className="inline-flex items-center gap-2 bg-white text-charcoal hover:bg-warm-sand transition-all duration-300 rounded-full px-8 py-3.5 font-body font-medium"
              >
                Experience Lalita
                <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-2xl mb-16"
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-charcoal mb-4">
              Full-Service Management
            </h2>
            <p className="font-body text-lg text-charcoal-light">
              Everything you need to maximize your villa's potential, all under one roof.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, idx) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-warm-sand p-8 rounded-2xl"
              >
                <service.icon className="w-10 h-10 text-ubud-jungle mb-4" />
                <h3 className="font-heading text-xl font-medium text-charcoal mb-2">
                  {service.title}
                </h3>
                <p className="font-body text-charcoal-light text-sm">
                  {service.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 lg:py-28 bg-warm-sand">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-charcoal mb-6">
                Why Choose Lalita?
              </h2>
              <p className="font-body text-lg text-charcoal-light mb-8">
                We're not the biggest management company in Bali—and that's by design. Our boutique approach means better service, more attention, and higher returns.
              </p>
              <ul className="space-y-4">
                {benefits.map((benefit) => (
                  <li key={benefit} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-ubud-jungle/10 flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-ubud-jungle" />
                    </div>
                    <span className="font-body text-charcoal">{benefit}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src={whyChooseImage}
                alt="Bali Property"
                className="rounded-3xl shadow-floating"
              />
              <div className="absolute -bottom-6 -right-6 bg-ubud-jungle text-white p-6 rounded-2xl shadow-lg">
                <div className="font-heading text-3xl font-semibold mb-1">40%</div>
                <div className="font-body text-white/80 text-sm">Average income increase</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-charcoal mb-4">
              How It Works
            </h2>
            <p className="font-body text-lg text-charcoal-light max-w-2xl mx-auto">
              Getting started with Lalita is simple. Here's what to expect.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Consultation', desc: 'We visit your property and discuss your goals.' },
              { step: '02', title: 'Proposal', desc: 'You receive a tailored management plan and pricing.' },
              { step: '03', title: 'Onboarding', desc: 'We photograph, list, and prepare your villa.' },
              { step: '04', title: 'Management', desc: 'Sit back while we handle everything.' },
            ].map((item, idx) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="text-center"
              >
                <div className="font-mono text-5xl text-ubud-jungle/20 font-bold mb-4">
                  {item.step}
                </div>
                <h3 className="font-heading text-xl font-medium text-charcoal mb-2">
                  {item.title}
                </h3>
                <p className="font-body text-charcoal-light text-sm">
                  {item.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stay with Us CTA */}
      <section className="py-20 lg:py-28 bg-warm-sand">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-charcoal mb-6">
                Stay with Us
              </h2>
              <p className="font-body text-lg text-charcoal-light mb-8">
                Experience the Lalita difference firsthand. Browse our curated collection of managed villas and book your perfect Bali getaway.
              </p>
              <Link
                to="/stays"
                className="inline-flex items-center gap-2 bg-ubud-jungle text-white hover:bg-ubud-jungle-dark transition-all duration-300 rounded-full px-8 py-3.5 font-body font-medium"
                data-testid="stay-with-us-btn"
              >
                Browse Short-term Rentals
                <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1602002418082-a4443e081dd1?w=800&q=80"
                alt="Luxury Villa Stay"
                className="rounded-3xl shadow-floating"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-ubud-jungle">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-white mb-6">
              Ready to maximize your villa's potential?
            </h2>
            <p className="font-body text-white/80 text-lg mb-8">
              Schedule a free property consultation with our team today.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-white text-ubud-jungle hover:bg-warm-sand transition-all duration-300 rounded-full px-8 py-4 font-body font-medium"
            >
              Get a Free Consultation
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PropertyManagementPage;
